import React from 'react';

export const compressionErrors = {
  id: 'troubleshooting-compression-errors',
  title: 'Compression Failed Errors',
  description: 'Common compression errors and how to resolve them.',
  category: 'troubleshooting',
  lastUpdated: '2025-06-29',
  tags: ['errors', 'compression', 'troubleshooting'],
  content: () => (
    <div>
      <div className="space-y-6">
        <p className="text-muted-foreground leading-relaxed">
          If your compression is failing, here are the most common causes and solutions to get 
          your GLB/GLTF files compressed successfully.
        </p>
        
        <div>
          <h2 className="text-xl font-semibold text-foreground mb-3">Common Error Messages</h2>
          
          <div className="space-y-4">
            <div className="bg-danger-soft border border-danger-border rounded-lg p-4">
              <h3 className="text-lg font-medium text-danger-muted mb-2">"File format not supported"</h3>
              <p className="text-muted-foreground mb-2">
                <strong>Cause:</strong> The uploaded file is not a valid GLB or GLTF file.
              </p>
              <p className="text-muted-foreground">
                <strong>Solution:</strong> Ensure your file has a .glb or .gltf extension and is properly formatted.
              </p>
            </div>
            
            <div className="bg-danger-soft border border-danger-border rounded-lg p-4">
              <h3 className="text-lg font-medium text-danger-muted mb-2">"File too large"</h3>
              <p className="text-muted-foreground mb-2">
                <strong>Cause:</strong> The file exceeds the 500MB size limit.
              </p>
              <p className="text-muted-foreground">
                <strong>Solution:</strong> Reduce file size by optimizing textures or geometry before uploading.
              </p>
            </div>
            
            <div className="bg-danger-soft border border-danger-border rounded-lg p-4">
              <h3 className="text-lg font-medium text-danger-muted mb-2">"Corrupted file data"</h3>
              <p className="text-muted-foreground mb-2">
                <strong>Cause:</strong> The file contains invalid or corrupted data.
              </p>
              <p className="text-muted-foreground">
                <strong>Solution:</strong> Re-export the file from your 3D software or validate it with our <a href="/gltf-validator" className="text-accent hover:text-accent/90">glTF validator</a>.
              </p>
            </div>
          </div>
        </div>
        
        <div>
          <h2 className="text-xl font-semibold text-foreground mb-3">Troubleshooting Steps</h2>
          <ol className="list-decimal list-inside text-muted-foreground space-y-2 ml-4">
            <li>Verify your file is a valid GLB or GLTF format</li>
            <li>Check that file size is under 500MB</li>
            <li>Try uploading with default compression settings first</li>
            <li>If using custom settings, reduce compression intensity</li>
            <li>Validate your file structure using our <a href="/gltf-validator" className="text-accent hover:text-accent/90">glTF validator</a></li>
            <li>Re-export from your 3D software if issues persist</li>
          </ol>
        </div>
        
        <div>
          <h2 className="text-xl font-semibold text-foreground mb-3">Prevention Tips</h2>
          <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
            <li>Always test files in our <a href="/gltf-validator" className="text-accent hover:text-accent/90">glTF validator</a> before uploading</li>
            <li>Use the latest export plugins for your 3D software</li>
            <li>Optimize textures and geometry in your source file</li>
            <li>Avoid extremely complex shader setups</li>
            <li>Keep file paths and names simple (no special characters)</li>
          </ul>
        </div>

        <div>
          <h2 className="text-xl font-semibold text-foreground mb-3">Still Having Issues?</h2>
          <p className="text-muted-foreground leading-relaxed">
            If you've tried all the above steps and are still experiencing compression errors, 
            please contact me at <a href="mailto:stan@compress-glb.com" className="text-accent hover:text-accent/90">stan@compress-glb.com</a>.
            {' '}Include details about:
          </p>
          <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4 mt-3">
            <li>The exact error message you're seeing</li>
            <li>Your file size and format</li>
            <li>The compression settings you tried</li>
            <li>Any error logs or screenshots</li>
          </ul>
          <p className="text-muted-foreground leading-relaxed mt-3">
            I'll help you resolve the issue as quickly as possible.
          </p>
        </div>
      </div>
    </div>
  )
}; 